import {
  AlertTriangle,
  ChevronDown,
  Clock,
  ExternalLink,
  FileText,
  Globe,
  ImagePlus,
  MapPin,
  MoreVertical,
  Phone,
  QrCode,
  Settings,
  Star,
} from "lucide-react";
import { DateTime, Info } from "luxon";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useGoogleBusinessConnection } from "@/domains/settings/features/integrations/google-business/hooks/use-google-business-connection";
import { useSalonStore } from "@/lib/store/salon";
import { cn } from "@/lib/utils";
import { type GbpInsights, useGbpInsights } from "../hooks/use-gbp-insights";
import { useGbpMedia } from "../hooks/use-gbp-media";
import { useSalonReviewStats } from "../hooks/use-salon-review-stats";
import { GbpAddPhotosDialog } from "./gbp-add-photos-dialog";
import { GbpMediaGalleryDialog } from "./gbp-media-gallery-dialog";
import { GbpPostUpdateDialog } from "./gbp-post-update-dialog";
import { GbpUpdateHoursDialog } from "./gbp-update-hours-dialog";
import { RequestReviewDialog } from "./request-review-dialog";

interface MetricConfig {
  key: "profileViews" | "searchImpressions" | "directionRequests" | "phoneCalls";
  label: string;
  valueCn: string;
  bgCn: string;
}

const METRICS: MetricConfig[] = [
  {
    key: "profileViews",
    label: "Profile Views",
    valueCn: "text-violet-600 dark:text-violet-400",
    bgCn: "bg-violet-50 dark:bg-violet-950/30",
  },
  {
    key: "searchImpressions",
    label: "Search Impressions",
    valueCn: "text-blue-600 dark:text-blue-400",
    bgCn: "bg-blue-50 dark:bg-blue-950/30",
  },
  {
    key: "directionRequests",
    label: "Direction Requests",
    valueCn: "text-emerald-600 dark:text-emerald-400",
    bgCn: "bg-emerald-50 dark:bg-emerald-950/30",
  },
  {
    key: "phoneCalls",
    label: "Phone Calls",
    valueCn: "text-rose-600 dark:text-rose-400",
    bgCn: "bg-rose-50 dark:bg-rose-950/30",
  },
];

// Temporarily hide the quick-actions row (Add Photos / Post Update / Share
// Review Link / Update Hours). Flip to `true` to bring it back.
const SHOW_QUICK_ACTIONS = false;

type ActiveDialog = "photos" | "post" | "hours" | "reviewLink" | null;

function GoogleColorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function SkeletonCard({ bgCn }: { bgCn: string }) {
  return (
    <div className={cn("rounded-xl p-4 space-y-2 animate-pulse", bgCn)}>
      <div className="h-3 w-24 rounded bg-current opacity-10" />
      <div className="h-8 w-16 rounded bg-current opacity-10" />
      <div className="h-3 w-20 rounded bg-current opacity-10" />
    </div>
  );
}

function formatPeriodLabel(
  start: string | null | undefined,
  end: string | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  if (!start || !end) return t("gbpInsights.periodLabelFallback");
  const days = Math.round(DateTime.fromISO(end).diff(DateTime.fromISO(start), "days").days) + 1;
  return t("gbpInsights.periodLabel", { days });
}

/** Renders a row of gold stars with half-star support using a clip technique. */
function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => {
        const fill = Math.min(1, Math.max(0, rating - (i - 1)));
        // full star
        if (fill >= 0.75) {
          return <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />;
        }
        // half star: overlay two stars, clip top star to 50% width
        if (fill >= 0.25) {
          return (
            <span key={i} className="relative w-3.5 h-3.5 inline-flex shrink-0">
              {/* empty background star */}
              <Star className="w-3.5 h-3.5 fill-neutral-200 text-neutral-200 absolute inset-0" />
              {/* half-filled foreground star */}
              <span className="absolute inset-0 overflow-hidden" style={{ width: "50%" }}>
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              </span>
            </span>
          );
        }
        // empty star
        return <Star key={i} className="w-3.5 h-3.5 fill-neutral-200 text-neutral-200" />;
      })}
    </div>
  );
}

/** Stylised static map tile — no real map API. */
function MockMapTile() {
  return (
    <div className="relative w-full h-full overflow-hidden" style={{ background: "#e8ead3" }}>
      {/* faint road lines */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 120 80"
        aria-hidden="true"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* horizontal roads */}
        <line x1="0" y1="40" x2="120" y2="40" stroke="#fff" strokeWidth="4" opacity="0.7" />
        <line x1="0" y1="22" x2="120" y2="22" stroke="#fff" strokeWidth="2" opacity="0.5" />
        <line x1="0" y1="60" x2="120" y2="60" stroke="#fff" strokeWidth="2" opacity="0.5" />
        {/* vertical roads */}
        <line x1="60" y1="0" x2="60" y2="80" stroke="#fff" strokeWidth="4" opacity="0.7" />
        <line x1="35" y1="0" x2="35" y2="80" stroke="#fff" strokeWidth="2" opacity="0.5" />
        <line x1="90" y1="0" x2="90" y2="80" stroke="#fff" strokeWidth="2" opacity="0.5" />
        {/* block fills */}
        <rect x="0" y="0" width="33" height="20" fill="#d4d8c0" opacity="0.6" />
        <rect x="37" y="0" width="21" height="20" fill="#d4d8c0" opacity="0.6" />
        <rect x="62" y="0" width="26" height="20" fill="#d4d8c0" opacity="0.6" />
        <rect x="0" y="24" width="33" height="14" fill="#d4d8c0" opacity="0.6" />
        <rect x="62" y="24" width="26" height="14" fill="#d4d8c0" opacity="0.6" />
        <rect x="92" y="24" width="28" height="14" fill="#d4d8c0" opacity="0.6" />
        <rect x="0" y="42" width="33" height="16" fill="#d4d8c0" opacity="0.6" />
        <rect x="37" y="42" width="21" height="16" fill="#d4d8c0" opacity="0.6" />
        <rect x="62" y="42" width="26" height="16" fill="#d4d8c0" opacity="0.6" />
        <rect x="92" y="42" width="28" height="16" fill="#d4d8c0" opacity="0.6" />
        <rect x="0" y="62" width="33" height="18" fill="#d4d8c0" opacity="0.6" />
        <rect x="37" y="62" width="21" height="18" fill="#d4d8c0" opacity="0.6" />
        <rect x="62" y="62" width="26" height="18" fill="#d4d8c0" opacity="0.6" />
        <rect x="92" y="62" width="28" height="18" fill="#d4d8c0" opacity="0.6" />
      </svg>
      {/* centered red pin */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative flex flex-col items-center">
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center shadow-md"
            style={{ background: "#ea4335" }}
          >
            <MapPin className="w-3.5 h-3.5 text-white fill-white" />
          </div>
          <div className="w-1.5 h-1.5 rounded-full mt-0.5" style={{ background: "#ea4335" }} />
        </div>
      </div>
    </div>
  );
}

/** The Google-panel-style preview block (always rendered on a light surface). */
function GbpKnowledgePanel({
  locationName,
  firstPhotoUrl,
  secondPhotoUrl,
  salonLatitude,
  salonLongitude,
  salonAddress,
  googleStats,
  profile,
  onPhotoClick,
}: {
  locationName: string;
  firstPhotoUrl: string | null;
  secondPhotoUrl: string | null;
  salonLatitude: number | null | undefined;
  salonLongitude: number | null | undefined;
  /** Fallback for the map tile when lat/lng is unavailable — a text address
   *  that Google Maps can search + zoom to (better than place_id embed which
   *  often defaults to a continent-level view for service-area businesses). */
  salonAddress: string | null | undefined;
  googleStats: { averageRating: number; count: number } | null | undefined;
  // GBP-sourced public details (phone/website/hours/address) — null in live mode.
  profile: NonNullable<GbpInsights>["profile"] | undefined;
  onPhotoClick: (index: number) => void;
}) {
  const { t, i18n } = useTranslation("reviews");
  const [hoursExpanded, setHoursExpanded] = useState(false);

  // Localised weekday names, Monday-first to match GBP ordering.
  const weekdayNames = Info.weekdays("long", { locale: i18n.language });
  const todayWeekday = DateTime.now().weekday; // 1 = Mon … 7 = Sun
  const todayHours = profile?.weeklyHours.find((h) => h.weekday === todayWeekday) ?? null;

  // Map-tile embed URL — best to worst, all no-API-key:
  // 1) lat/lng → pinpoint marker on the exact spot
  // 2) text address → Google searches + zooms to street level
  // Note: place_id with output=embed zooms to continent-level and ignores &z;
  // we intentionally skip it here. The "View on Google" button still uses
  // place_id and opens in a new tab where zoom works correctly.
  const mapTileUrl = (() => {
    if (salonLatitude != null && salonLongitude != null) {
      return `https://maps.google.com/maps?q=${salonLatitude},${salonLongitude}&output=embed`;
    }
    if (salonAddress) {
      return `https://maps.google.com/maps?q=${encodeURIComponent(salonAddress)}&output=embed`;
    }
    return null;
  })();

  return (
    /* Force light surface even in dark mode — simulates an embedded Google panel */
    <div className="rounded-xl border border-neutral-200 bg-white text-neutral-900 shadow-sm overflow-hidden text-[13px]">
      {/* Photo collage */}
      <div className="w-full h-44 flex gap-[2px] bg-neutral-200">
        {/* Left cell: cover photo (~40% width) */}
        <button
          type="button"
          className="relative cursor-pointer"
          style={{ width: "40%" }}
          onClick={() => onPhotoClick(0)}
        >
          {firstPhotoUrl ? (
            <img src={firstPhotoUrl} alt={locationName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-neutral-100 flex items-center justify-center">
              <MapPin className="w-8 h-8 text-neutral-300" />
            </div>
          )}
        </button>

        {/* Right column: two stacked cells (~60% width) */}
        <div className="flex flex-col gap-[2px]" style={{ flex: 1 }}>
          {/* Top: map tile — not clickable, the iframe handles its own interaction */}
          <div className="flex-1 overflow-hidden">
            {mapTileUrl ? (
              <iframe
                src={mapTileUrl}
                title={t("gbpInsights.mapTileAlt")}
                className="w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <MockMapTile />
            )}
          </div>

          {/* Bottom: exterior photo */}
          <button
            type="button"
            className="relative flex-1 overflow-hidden cursor-pointer"
            onClick={() => onPhotoClick(1)}
          >
            {secondPhotoUrl ? (
              <img
                src={secondPhotoUrl}
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-neutral-100 flex items-center justify-center">
                <ImagePlus className="w-6 h-6 text-neutral-300" />
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Panel body */}
      <div className="px-4 pt-3 pb-4 space-y-3">
        {/* Business name */}
        <h3 className="text-xl font-semibold leading-tight line-clamp-2 text-neutral-900">
          {locationName}
        </h3>

        {/* Rating row */}
        {googleStats && googleStats.count > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold tabular-nums text-neutral-800">
              {googleStats.averageRating.toFixed(1)}
            </span>
            <StarRating rating={googleStats.averageRating} />
            <span className="text-[#1a73e8] text-xs font-medium hover:underline cursor-default">
              {t("gbpInsights.googleReviewsCount", { count: googleStats.count })}
            </span>
            <button
              type="button"
              className="ml-auto text-neutral-500 hover:text-neutral-700 cursor-default"
              tabIndex={-1}
              aria-hidden="true"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Category line (from Google) */}
        {profile?.category && (
          <p className="text-neutral-500 text-xs leading-tight">{profile.category}</p>
        )}

        {/* Divider */}
        <div className="border-t border-neutral-200" />

        {/* Details — values exactly as they appear on Google */}
        <div className="space-y-2.5">
          {/* Address */}
          {profile?.address && (
            <div className="flex items-start gap-2.5 text-neutral-700">
              <MapPin className="w-4 h-4 mt-px shrink-0 text-neutral-500" />
              <span className="leading-snug">{profile.address}</span>
            </div>
          )}

          {/* Website — owner can verify the linked site at a glance */}
          {profile?.website && (
            <a
              href={profile.websiteUrl ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("gbpInsights.ctaWebsite")}
              className="group flex items-center gap-2.5 text-neutral-700"
            >
              <Globe className="w-4 h-4 shrink-0 text-neutral-500" />
              <span className="truncate text-[#1a73e8] group-hover:underline">
                {profile.website}
              </span>
            </a>
          )}

          {/* Phone */}
          {profile?.phone && (
            <a
              href={`tel:${profile.phone.replace(/\s+/g, "")}`}
              aria-label={t("gbpInsights.ctaCall")}
              className="group flex items-center gap-2.5 text-neutral-700"
            >
              <Phone className="w-4 h-4 shrink-0 text-neutral-500" />
              <span className="text-[#1a73e8] group-hover:underline">{profile.phone}</span>
            </a>
          )}

          {/* Hours — expandable weekly schedule */}
          {profile && profile.weeklyHours.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setHoursExpanded((v) => !v)}
                aria-expanded={hoursExpanded}
                className="flex w-full items-center gap-2.5 text-left text-neutral-700"
              >
                <Clock className="w-4 h-4 shrink-0 text-neutral-500" />
                <span className="flex-1">
                  <span className="font-semibold">{t("gbpInsights.hoursLabel")}</span>{" "}
                  {todayHours && !todayHours.closed ? (
                    <>
                      <span className="font-medium text-green-700">
                        {t("gbpInsights.hoursOpen")}
                      </span>
                      {todayHours.close && (
                        <span className="text-neutral-600">
                          {" · "}
                          {t("gbpInsights.hoursCloses", { time: todayHours.close })}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="font-medium text-neutral-500">
                      {t("gbpInsights.hoursClosed")}
                    </span>
                  )}
                </span>
                <ChevronDown
                  className={cn(
                    "w-4 h-4 shrink-0 text-neutral-500 transition-transform",
                    hoursExpanded && "rotate-180"
                  )}
                />
              </button>

              {hoursExpanded && (
                <ul className="mt-2 ml-[26px] space-y-1.5">
                  {profile.weeklyHours.map((h) => {
                    const isToday = h.weekday === todayWeekday;
                    return (
                      <li
                        key={h.weekday}
                        className={cn(
                          "flex justify-between gap-6 text-xs",
                          isToday ? "font-semibold text-neutral-900" : "text-neutral-600"
                        )}
                      >
                        <span className="capitalize">{weekdayNames[h.weekday - 1] ?? ""}</span>
                        <span className={cn("tabular-nums", h.closed && "text-neutral-400")}>
                          {h.closed ? t("gbpInsights.hoursClosed") : `${h.open} – ${h.close}`}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * GBP card: business identity sidebar + performance metrics + quick actions.
 * Renders nothing when the salon has no GBP connection or the insights query errors.
 */
export function GbpInsightsSection() {
  const { t } = useTranslation("reviews");
  const { insights, loading } = useGbpInsights();
  const { connection } = useGoogleBusinessConnection();
  const { stats: googleStats } = useSalonReviewStats("GOOGLE");
  const { media, firstPhotoUrl, secondPhotoUrl } = useGbpMedia();
  const currentSalon = useSalonStore((s) => s.currentSalon);
  const lgRef = useRef<import("lightgallery/lightgallery").LightGallery | null>(null);
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);

  // On first load: loading=true, insights=null → show skeletons.
  // No connection: loading=false, insights=null → render nothing.
  if (!loading && !insights) return null;

  const placeId = connection?.placeId;
  const locationName = connection?.locationName ?? "";
  const reauthRequired = connection?.reauthRequired ?? false;
  const syncEnabled = connection?.syncEnabled ?? true;

  const salonLat = currentSalon?.latitude;
  const salonLng = currentSalon?.longitude;
  const mapsUrl =
    salonLat != null && salonLng != null
      ? `https://www.google.com/maps?q=${salonLat},${salonLng}&z=17`
      : placeId
        ? `https://www.google.com/maps/place/?q=place_id:${placeId}`
        : null;

  const periodLabel = formatPeriodLabel(
    insights?.currentPeriodStart,
    insights?.currentPeriodEnd,
    t
  );

  return (
    <>
      <div>
        {/* Header — identity on the left, actions on the right; stacks on mobile */}
        <div className="flex flex-col gap-3.5 px-2 py-3.5 sm:px-5 md:flex-row md:items-center md:justify-between md:gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted/60 ring-1 ring-border/60">
              <GoogleColorIcon className="h-4.5 w-4.5" />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold tracking-tight">
                  {t("gbpInsights.headerTitle")}
                </span>
                {reauthRequired ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400">
                    <AlertTriangle className="h-3 w-3" />
                    {t("gbpInsights.badgeNeedsAttention")}
                  </span>
                ) : !syncEnabled ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
                    {t("gbpInsights.badgeSyncPaused")}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    </span>
                    {t("gbpInsights.badgeLive")}
                  </span>
                )}
              </div>
              {locationName && (
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{locationName}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 md:shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="h-8 flex-1 justify-center gap-1.5 text-xs md:flex-none"
              asChild
            >
              <Link to="/settings/google-business">
                <Settings className="h-3.5 w-3.5" />
                {t("gbpInsights.editProfile")}
              </Link>
            </Button>
            {mapsUrl && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 flex-1 justify-center gap-1.5 text-xs md:flex-none"
                asChild
              >
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                  {t("gbpInsights.viewOnGoogle")}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </Button>
            )}
          </div>
        </div>

        {/* Body: sidebar + content — stacks on mobile, side-by-side on md+ */}
        <div className="flex flex-col md:flex-row">
          {/* Left sidebar: Google-style knowledge panel preview */}
          <div className="flex flex-col shrink-0 px-2 py-4 w-full sm:p-4 md:w-80">
            <p className="text-xs font-semibold tracking-widest text-muted-foreground uppercase mb-3">
              {t("gbpInsights.howYouAppear")}
            </p>
            <GbpKnowledgePanel
              locationName={locationName}
              firstPhotoUrl={firstPhotoUrl}
              secondPhotoUrl={secondPhotoUrl}
              salonLatitude={currentSalon?.latitude}
              salonLongitude={currentSalon?.longitude}
              salonAddress={
                currentSalon
                  ? [
                      currentSalon.address,
                      currentSalon.city,
                      currentSalon.state,
                      currentSalon.country,
                    ]
                      .filter(Boolean)
                      .join(", ")
                  : null
              }
              googleStats={googleStats}
              profile={insights?.profile}
              onPhotoClick={(index) => lgRef.current?.openGallery(index)}
            />
            <GbpMediaGalleryDialog
              media={media}
              onInit={(instance) => {
                lgRef.current = instance;
              }}
            />
          </div>

          {/* Separator — horizontal on mobile, vertical on md+ */}
          <div className="bg-border shrink-0 h-px mx-5 md:h-auto md:w-px md:mx-0 md:my-4" />

          {/* Right: metrics + quick actions — fills the column height on desktop */}
          <div className="flex flex-1 flex-col gap-6 px-2 py-4 sm:p-5">
            <div className="space-y-4">
              <p className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
                {periodLabel}
              </p>

              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {METRICS.map((m) => {
                  if (loading && !insights) {
                    return <SkeletonCard key={m.key} bgCn={m.bgCn} />;
                  }

                  const metric = insights?.[m.key];
                  const pct = metric?.changePercent ?? null;
                  const isPositive = pct !== null && pct >= 0;
                  const pctDisplay =
                    pct === null ? null : `${isPositive ? "+" : ""}${pct.toFixed(0)}%`;

                  return (
                    <div key={m.key} className={cn("min-w-0 rounded-xl p-4", m.bgCn)}>
                      <p className="mb-1 text-xs font-medium leading-tight text-muted-foreground">
                        {t(`gbpInsights.metrics.${m.key}`)}
                      </p>
                      <p className={cn("text-2xl font-bold leading-none tabular-nums", m.valueCn)}>
                        {metric ? new Intl.NumberFormat("en-US").format(metric.current) : "—"}
                      </p>
                      {pctDisplay !== null && (
                        <p
                          className={cn(
                            "mt-1.5 text-xs font-medium",
                            isPositive
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-rose-600 dark:text-rose-400"
                          )}
                        >
                          {pctDisplay}{" "}
                          <span className="font-normal text-muted-foreground">
                            {t("gbpInsights.vsPeriod")}
                          </span>
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Get review link — the one quick action kept visible (moved here
                  from the Customer feedback card). Sits right under the metrics. */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                className="h-auto min-h-9 min-w-0 justify-start gap-2 whitespace-normal py-2 text-left leading-tight text-amber-600 border-amber-200 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-800 dark:hover:bg-amber-950/30"
                onClick={() => setActiveDialog("reviewLink")}
                disabled={!placeId}
              >
                <QrCode className="w-4 h-4 shrink-0" />
                {t("gbpInsights.shareReviewLink")}
              </Button>
            </div>

            {/* Other quick actions temporarily hidden — flip SHOW_QUICK_ACTIONS to restore. */}
            {SHOW_QUICK_ACTIONS && (
              <div className="space-y-3">
                <p className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
                  {t("gbpInsights.quickActions")}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    className="h-auto min-h-9 min-w-0 justify-start gap-2 whitespace-normal py-2 text-left text-violet-600 leading-tight dark:text-violet-400 border-violet-200 dark:border-violet-800 hover:bg-violet-50 dark:hover:bg-violet-950/30"
                    onClick={() => setActiveDialog("photos")}
                  >
                    <ImagePlus className="w-4 h-4 shrink-0" />
                    {t("gbpInsights.addPhotos")}
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto min-h-9 min-w-0 justify-start gap-2 whitespace-normal py-2 text-left text-emerald-600 leading-tight dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                    onClick={() => setActiveDialog("post")}
                  >
                    <FileText className="w-4 h-4 shrink-0" />
                    {t("gbpInsights.postUpdate")}
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto min-h-9 min-w-0 justify-start gap-2 whitespace-normal py-2 text-left text-rose-600 leading-tight dark:text-rose-400 border-rose-200 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                    onClick={() => setActiveDialog("hours")}
                  >
                    <Clock className="w-4 h-4 shrink-0" />
                    {t("gbpInsights.updateHours")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <GbpAddPhotosDialog
        open={activeDialog === "photos"}
        onOpenChange={(open) => !open && setActiveDialog(null)}
      />
      <GbpPostUpdateDialog
        open={activeDialog === "post"}
        onOpenChange={(open) => !open && setActiveDialog(null)}
      />
      <GbpUpdateHoursDialog
        open={activeDialog === "hours"}
        onOpenChange={(open) => !open && setActiveDialog(null)}
      />
      {placeId && (
        <RequestReviewDialog
          open={activeDialog === "reviewLink"}
          onOpenChange={(open) => !open && setActiveDialog(null)}
          placeId={placeId}
          locationName={locationName}
        />
      )}
    </>
  );
}
